import React, { useEffect, useState } from 'react';
import { Modal, Form, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { updateTool, replaceToolImage } from '../../../services/toolAssignmentService';
import { listToolCategories } from '../../../services/toolCategoryService';
import ToolPhotoInput from './ToolPhotoInput';
import LocationPicker from '../../../components/locations/LocationPicker';

const ToolEditModal = ({ show, onHide, tool }) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [error, setError] = useState('');

  const { data: catsData } = useQuery({
    queryKey: ['tool-categories'],
    queryFn: () => listToolCategories({ active_only: true }),
    enabled: show,
    staleTime: 5 * 60 * 1000
  });
  const categories = catsData?.data || [];

  useEffect(() => {
    if (!tool) return;
    setName(tool.name || '');
    setCategoryId(tool.category_id || '');
    setLocationId(tool.location_id || '');
    setBrand(tool.brand || '');
    setModel(tool.model || '');
    setDescription(tool.description || '');
    setNotes(tool.notes || '');
    setExistingImage(tool.image_url || null);
    setImageFile(null);
    setImagePreview(null);
    setRequiresApproval(tool.requires_approval === true);
    setError('');
  }, [tool]);

  const mutation = useMutation({
    mutationFn: async () => {
      const metaResult = await updateTool(tool.id, {
        name,
        category_id: categoryId || null,
        location_id: locationId || null,
        brand: brand || null,
        model: model || null,
        description: description || null,
        notes: notes || null,
        requires_approval: requiresApproval
      });
      if (metaResult.error) throw metaResult.error;

      if (imageFile) {
        const imgResult = await replaceToolImage(tool.id, imageFile, existingImage);
        if (imgResult.error) throw imgResult.error;
        return imgResult.data;
      }
      return metaResult.data;
    },
    onSuccess: () => {
      toast.success('Herramienta actualizada.');
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      queryClient.invalidateQueries({ queryKey: ['all-tools'] });
      queryClient.invalidateQueries({ queryKey: ['tool-detail', tool?.id] });
      onHide();
    },
    onError: (e) => setError(e.message)
  });

  if (!tool) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-pencil me-2" />Editar herramienta
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="mb-3 small text-muted">
          Serial: <span className="font-monospace">{tool.serial_number}</span>
          {' · '}
          ID: <span className="font-monospace">{tool.id?.slice(0, 8)}</span>
        </div>
        <Row>
          <Col md={3} className="d-flex flex-column align-items-center mb-3">
            <Form.Label className="w-100">Foto</Form.Label>
            <ToolPhotoInput
              file={imageFile}
              previewUrl={imagePreview || existingImage}
              onChange={(f, url) => { setImageFile(f); setImagePreview(url); }}
              onRemove={() => { setImageFile(null); setImagePreview(null); setExistingImage(null); }}
              disabled={mutation.isPending}
            />
          </Col>
          <Col md={9}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    value={name} onChange={e => setName(e.target.value)}
                    disabled={mutation.isPending}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Categoría</Form.Label>
                  <Form.Select
                    value={categoryId}
                    onChange={e => setCategoryId(e.target.value)}
                    disabled={mutation.isPending}
                  >
                    <option value="">Sin categoría</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.label_es}</option>
                    ))}
                    {tool?.category && !tool?.category_id && (
                      <option value="" disabled>— Legado: "{tool.category}"</option>
                    )}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    <Link to="/herramientas/categorias" target="_blank" rel="noopener">¿Falta una categoría?</Link>
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ubicación</Form.Label>
                  <LocationPicker
                    value={locationId}
                    onChange={setLocationId}
                    disabled={mutation.isPending}
                    placeholder="Sin ubicación"
                    showCount={false}
                  />
                  <Form.Text className="text-muted">
                    <Link to="/admin/locations" target="_blank" rel="noopener">Gestionar ubicaciones</Link>
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Marca</Form.Label>
                  <Form.Control
                    value={brand} onChange={e => setBrand(e.target.value)}
                    disabled={mutation.isPending}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Modelo</Form.Label>
                  <Form.Control
                    value={model} onChange={e => setModel(e.target.value)}
                    disabled={mutation.isPending}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Col>
        </Row>
        <Form.Group className="mb-3">
          <Form.Label>Descripción</Form.Label>
          <Form.Control
            as="textarea" rows={2}
            value={description} onChange={e => setDescription(e.target.value)}
            disabled={mutation.isPending}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Notas internas</Form.Label>
          <Form.Control
            as="textarea" rows={2}
            value={notes} onChange={e => setNotes(e.target.value)}
            disabled={mutation.isPending}
          />
        </Form.Group>

        <Form.Group className="border rounded p-3 bg-light">
          <Form.Check
            type="switch"
            id="edit-tool-requires-approval"
            label={
              <span>
                <i className="bi bi-shield-check me-1 text-warning" />
                <strong>Requiere aprobación para préstamo</strong>
              </span>
            }
            checked={requiresApproval}
            onChange={e => setRequiresApproval(e.target.checked)}
            disabled={mutation.isPending}
          />
          <Form.Text className="text-muted d-block mt-1">
            Si está activo, los técnicos deben enviar una solicitud que un
            supervisor apruebe antes de poder tomar esta herramienta.
            Útil para equipos caros, calibrados o críticos.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={mutation.isPending}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          disabled={!name.trim() || mutation.isPending}
          onClick={() => { setError(''); mutation.mutate(); }}
        >
          {mutation.isPending && <Spinner size="sm" animation="border" className="me-1" />}
          Guardar cambios
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ToolEditModal;
